import { useDispatch } from 'react-redux';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import {
  updateApplicationStatusWithEdgeFunction,
} from '../api/applicationsWithEdgeFunction';
import {
  updateInterviewWithEdgeFunction,
  createInterviewWithEdgeFunction,
  createInterviewBulkWithEdgeFunction,
} from '../api/interviewsWithEdgeFunction';
import {
  createOfferLetterWithEdgeFunction,
  getUploadUrlsWithEdgeFunction,
  uploadOfferPdfToSignedUrl,
} from '../api/offerLettersWithEdgeFunction';
import { updateJobStatusWithEdgeFunction } from '../../Dashboard/api/jobsWithEdgeFunction';
import {
  Candidate,
  InterviewItem,
  InterviewStatus,
  CreateInterviewPayload,
  UpdateInterviewPayload,
  CreateInterviewBulkPayload,
  KanbanColumns,
  KanbanColumnId,
  CreateOfferLetterPayload,
  GeneratedOffer,
} from '../types';
import { ATS_CONSTANTS } from '../constants';
import { JobInfo } from './useATSData';
import { setGlobalLoading } from '../../../store/slices/uiSlice';

export const useATSActions = (
  jobId: string | undefined,
  triggerReload: () => void,
  jobInfo: JobInfo,
  setJobInfo: React.Dispatch<React.SetStateAction<JobInfo>>,
  columns: KanbanColumns,
  setColumns: React.Dispatch<React.SetStateAction<KanbanColumns>>,
  onOfferSuccess?: () => void
) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  // Helper to find candidate
  const findCandidate = (candidateId: string) => {
    for (const column of Object.values(columns)) {
      const itemIndex = column.items.findIndex((item) => item.id === candidateId);
      if (itemIndex !== -1) {
        return { columnId: column.id, itemIndex, candidate: column.items[itemIndex] };
      }
    }
    return null;
  };

  // Optimistic UI update helper
  const handleProgrammaticMove = (candidate: Candidate, targetColumnId: string) => {
    const source = findCandidate(candidate.id);
    if (!source) return;

    const sourceColumnId = source.columnId;
    if (sourceColumnId === targetColumnId) return;

    const sourceColumn = columns[sourceColumnId as KanbanColumnId];
    const targetColumn = columns[targetColumnId as KanbanColumnId];
    const sourceItems = [...sourceColumn.items];
    const targetItems = [...targetColumn.items];
    const [movedItem] = sourceItems.splice(source.itemIndex, 1);

    movedItem.column = targetColumnId as KanbanColumnId;

    setColumns({
      ...columns,
      [sourceColumnId]: { ...sourceColumn, items: sourceItems },
      [targetColumnId]: { ...targetColumn, items: targetItems },
    });
  };

  const handleUpdateApplicationStatus = async (candidate: Candidate, newStatus: string) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await updateApplicationStatusWithEdgeFunction({
        application_id: candidate.id,
        status: newStatus,
      });

      if (error) {
        showSnackbar('Failed to update status', 'error');
        return;
      }

      // Optimistic update
      let targetColumnId: KanbanColumnId = ATS_CONSTANTS.COLUMNS.APPLIED;
      if (newStatus === 'IN_CONSIDERATION') targetColumnId = ATS_CONSTANTS.COLUMNS.IN_CONSIDERATION;
      else if (newStatus === 'ACCEPTED') targetColumnId = ATS_CONSTANTS.COLUMNS.ACCEPTED;
      else if (newStatus === 'REJECTED') targetColumnId = ATS_CONSTANTS.COLUMNS.REJECTED;

      handleProgrammaticMove(candidate, targetColumnId);
      showSnackbar('Status updated successfully', 'success');
      triggerReload();
    } catch (err: any) {
      showSnackbar('Failed to update status', 'error');
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleRejectCandidate = async (candidate: InterviewItem) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await updateApplicationStatusWithEdgeFunction({
        application_id: candidate.id,
        status: ATS_CONSTANTS.STATUS.REJECTED,
      });
      if (error) {
        showSnackbar('Failed to reject candidate', 'error');
        dispatch(setGlobalLoading({ isLoading: false }));
        return;
      }
      // Optimistic update if it's a Candidate
      if ('column' in candidate) {
        handleProgrammaticMove(candidate as Candidate, ATS_CONSTANTS.COLUMNS.REJECTED);
      }
      showSnackbar('Candidate rejected', 'success');
      triggerReload();
    } catch (err: any) {
      showSnackbar('Failed to reject candidate', 'error');
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleScheduleInterview = async (payload: CreateInterviewPayload | UpdateInterviewPayload, isEditMode: boolean) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    if (isEditMode) {
      try {
        const { error } = await updateInterviewWithEdgeFunction(payload as UpdateInterviewPayload);
        if (error) throw error;
        showSnackbar('Interview rescheduled successfully', 'success');
        triggerReload();
        return true;
      } catch (err: any) {
        showSnackbar('Failed to reschedule interview', 'error');
        return false;
      } finally {
        dispatch(setGlobalLoading({ isLoading: false }));
      }
    } else {
      try {
        const { error } = await createInterviewWithEdgeFunction(payload as CreateInterviewPayload);
        if (error) throw error;
        showSnackbar('Interview scheduled successfully', 'success');
        triggerReload();
        return true;
      } catch (err: any) {
        showSnackbar('Failed to schedule interview', 'error');
        return false;
      } finally {
        dispatch(setGlobalLoading({ isLoading: false }));
      }
    }
  };

  const handleBulkScheduleInterviewSubmit = async (payload: CreateInterviewBulkPayload) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await createInterviewBulkWithEdgeFunction(payload);
      if (error) throw error;
      showSnackbar('Interviews scheduled successfully', 'success');
      triggerReload();
      return true;
    } catch (err: any) {
      showSnackbar('Failed to schedule interviews', 'error');
      return false;
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleUpdateInterviewStatus = async (candidate: InterviewItem, status: string) => {
    if (!candidate.interviewId) return;
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await updateInterviewWithEdgeFunction({
        interview_id: candidate.interviewId || '',
        status: status as InterviewStatus,
      });
      if (error) throw error;
      showSnackbar(`Interview marked as ${status.replace('_', ' ').toLowerCase()}`, 'success');
      triggerReload();
    } catch (err: any) {
      showSnackbar('Failed to update interview status', 'error');
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleCancelInterviewClick = async (candidate: InterviewItem) => {
    if (!candidate.interviewId) return;
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await updateInterviewWithEdgeFunction({
        interview_id: candidate.interviewId || '',
        status: ATS_CONSTANTS.STATUS.CANCELLED,
      });
      if (error) throw error;
      showSnackbar('Interview cancelled', 'success');
      triggerReload();
    } catch (err: any) {
      showSnackbar('Failed to cancel interview', 'error');
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleCreateOffer = async (generatedOffers: GeneratedOffer[]) => {
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      // Step 1: Get upload URLs
      const uploadUrlsPayload = {
        requests: generatedOffers.map(go => ({
          application_id: go.applicationId,
          file_name: go.filename,
        })),
        bucket_name: 'offers',
      };

      const { data: uploadUrls, error: uploadUrlsError } = await getUploadUrlsWithEdgeFunction(uploadUrlsPayload);

      if (uploadUrlsError || !uploadUrls) {
        showSnackbar('Failed to get upload permissions', 'error');
        return { success: false, failedIds: [] };
      }

      // Step 2: Upload PDFs
      const uploadPromises = generatedOffers.map(async (go) => {
        const uploadInfo = uploadUrls.find(u => u.application_id === go.applicationId);
        if (!uploadInfo) return { applicationId: go.applicationId, success: false };

        const file = new File([go.blob], go.filename, { type: 'application/pdf' });
        const success = await uploadOfferPdfToSignedUrl(uploadInfo.path, uploadInfo.token, file);
        return { applicationId: go.applicationId, success };
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(r => r.success);
      const failedUploadIds = uploadResults.filter(r => !r.success).map(r => r.applicationId);

      if (successfulUploads.length === 0) {
        showSnackbar('Failed to upload offer letters', 'error');
        return { success: false, failedIds: generatedOffers.map(go => go.applicationId) };
      }

      // Step 3: Link offer letters in database
      const createPayloads: CreateOfferLetterPayload[] = successfulUploads.map(su => {
        const go = generatedOffers.find(o => o.applicationId === su.applicationId)!;
        return {
          application_id: su.applicationId,
          file_name: go.filename,
          fee_amount: go.feeAmount,
          date_of_joining: go.dateOfJoining,
        };
      });

      const { data, error } = await createOfferLetterWithEdgeFunction(createPayloads);

      if (error) {
        showSnackbar('Failed to finalize offer letters', 'error');
        return { success: false, failedIds: successfulUploads.map(su => su.applicationId) };
      }

      if (data) {
        const totalRequested = generatedOffers.length;
        const actuallySuccessfulCount = successfulUploads.length - (data.failed_application_ids?.length || 0);
        const failedCount = totalRequested - actuallySuccessfulCount;
        const failedIds = [...failedUploadIds, ...(data.failed_application_ids || [])];

        if (actuallySuccessfulCount > 0) {
          if (failedCount === 0) {
            showSnackbar('All offer letters generated and saved successfully', 'success');
          } else {
            showSnackbar(`Generated ${actuallySuccessfulCount} offers, but ${failedCount} failed.`, 'info');
          }
          if (onOfferSuccess) onOfferSuccess();
          triggerReload();
          return { success: true, failedIds };
        } else {
          showSnackbar('Failed to generate any offer letters', 'error');
          return { success: false, failedIds: generatedOffers.map(go => go.applicationId) };
        }
      }
      return { success: false, failedIds: [] };
    } catch (err: any) {
      console.error('[handleCreateOffer] Error:', err);
      showSnackbar('An unexpected error occurred during offer creation', 'error');
      return { success: false, failedIds: [] };
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  const handleCloseJob = async () => {
    if (!jobId) return;
    dispatch(setGlobalLoading({ isLoading: true }));
    try {
      const { error } = await updateJobStatusWithEdgeFunction(jobId, 'CLOSED');
      if (error) throw error;
      showSnackbar('Job closed successfully', 'success');
      setJobInfo((prev) => ({ ...prev, status: 'Closed' }));
      return true;
    } catch (err: any) {
      showSnackbar('Failed to close job', 'error');
      return false;
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  };

  return {
    handleUpdateApplicationStatus,
    handleRejectCandidate,
    handleScheduleInterview,
    handleBulkScheduleInterviewSubmit,
    handleUpdateInterviewStatus,
    handleCancelInterviewClick,
    handleCreateOffer,
    handleCloseJob,
    findCandidate,
  };
};
